/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { Box, Divider, TextField, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';

import { IScheduleDataProps } from './CreateSchedule.Model';
import { CreateScheduleFieldNames } from '../AllJobs.Model';

import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';

import ScheduleService from './ScheduleService';
import PunchInOutGracePeriod from './PunchInOutGracePeriod';
import DateSelection, { mandatoryField } from '../../../../../Shared/Components/Common/DatePicker/DateSelection';

import theme from '../../../../../Shared/Themes/theme';
import {
  buttonStyles,
  DateFieldWrapper,
  JobNameContainer,
  JobNameWrapper,
} from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { Styles } from './CreateSchedules.Style';

const ScheduleDetails: React.FC<IScheduleDataProps> = ({
  t,
  control,
  service,
  setService,
  serviceTasks,
  setServiceTasks,
  punchInHours,
  setPunchInHours,
  punchInMinutes,
  setPunchInMinutes,
  punchOutHours,
  setPunchOutHours,
  punchOutMinutes,
  setPunchOutMinutes,
  endDateOffSet,
  setEndDateOffSet,
  customerId,
  jobEndDate,
}) => {
  const { headingText, textField, divider } = buttonStyles;
  const { marginBottom } = Styles;

  return (
    <>
      <Box sx={{ flex: 1, marginRight: '1.5rem' }}>
        <Typography sx={headingText}>{t('jobManagement:scheduleDates')}</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}>
          <Box sx={{ ...textField, ...marginBottom }}>
            <DateSelection
              requiredText={t('oneTimeJob:startDateFieldReq')}
              control={control}
              fieldName={CreateScheduleFieldNames.scheduleStartDate}
              label={t('jobManagement:scheduleStartDate')}
              iconColor="black"
              isExcludePastDate={true}
              showSpecificRangeDates={{
                from: new Date(),
                to: jobEndDate,
              }}
            />
          </Box>
          <Box sx={{ ...textField, ...marginBottom }}>
            <DateSelection
              requiredText={t('oneTimeJob:endDateFieldReq')}
              control={control}
              fieldName={CreateScheduleFieldNames.scheduleEndDate}
              label={t('jobManagement:scheduleEndDate')}
              iconColor="black"
              isExcludePastDate={true}
              showSpecificRangeDates={{
                from: new Date(),
                to: jobEndDate,
              }}
              isOptional={true}
            />
          </Box>
        </Box>
      </Box>
      <Divider sx={divider} />
      <Box>
        <Typography sx={headingText}>{t('oneTimeJob:serviceRequired')}</Typography>
        <ScheduleService
          service={service}
          setService={setService}
          serviceTasks={serviceTasks}
          setServiceTasks={setServiceTasks}
          customerId={customerId}
        />
      </Box>
      <Divider sx={divider} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={headingText}>{t('jobManagement:shiftTime')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
          <Box sx={{ ...textField, ...marginBottom }}>
            <Controller
              control={control}
              name={CreateScheduleFieldNames.shiftStartTime}
              render={({ field: { onChange, value } }) => (
                <DateFieldWrapper>
                  <TimePicker
                    label={
                      <span>
                        {t('calendar:startTime')}
                        <span style={mandatoryField}>*</span>
                      </span>
                    }
                    value={value ? dayjs(value, 'HH:mm') : null}
                    onChange={(newValue) => {
                      const formatted = newValue ? newValue.format('HH:mm') : '';
                      onChange(formatted);
                    }}
                    slotProps={{
                      textField: { sx: textField },
                      popper: {
                        sx: {
                          '.MuiPaper-root': {
                            boxShadow: BoxShadow.BoxShadow,
                            '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                              backgroundColor: theme.palette.primary.main,
                              fontWeight: theme.typography.fontWeightBold,
                            },
                            '.MuiMultiSectionDigitalClockSection-item:hover': {
                              backgroundColor: theme.palette.secondary.dark,
                            },
                            '.MuiButton-root': {
                              color: theme.palette.common.white,
                              backgroundColor: theme.palette.primary.dark,
                            },
                          },
                        },
                      },
                    }}
                  />
                </DateFieldWrapper>
              )}
            />
          </Box>
          <Box sx={{ ...textField, ...marginBottom }}>
            <Controller
              control={control}
              name={CreateScheduleFieldNames.shiftEndTime}
              render={({ field: { onChange, value } }) => (
                <DateFieldWrapper>
                  <TimePicker
                    label={
                      <span>
                        {t('calendar:endTime')}
                        <span style={mandatoryField}>*</span>
                      </span>
                    }
                    value={value ? dayjs(value, 'HH:mm') : null}
                    onChange={(newValue) => {
                      const formatted = newValue ? newValue.format('HH:mm') : '';
                      onChange(formatted);
                    }}
                    slotProps={{
                      textField: { sx: textField },
                      popper: {
                        sx: {
                          '.MuiPaper-root': {
                            boxShadow: BoxShadow.BoxShadow,
                            '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                              backgroundColor: theme.palette.primary.main,
                              fontWeight: theme.typography.fontWeightBold,
                            },
                            '.MuiMultiSectionDigitalClockSection-item:hover': {
                              backgroundColor: theme.palette.secondary.dark,
                            },
                            '.MuiButton-root': {
                              color: theme.palette.common.white,
                              backgroundColor: theme.palette.primary.dark,
                            },
                          },
                        },
                      },
                    }}
                  />
                </DateFieldWrapper>
              )}
            />
          </Box>
          <JobNameContainer>
            <JobNameWrapper>
              <TextField
                sx={textField}
                id="outlined-required"
                type="number"
                inputProps={{ min: 0 }}
                label={<span>{t('jobManagement:daysExtendedBy')}</span>}
                value={endDateOffSet}
                onChange={(event) => {
                  const value = event?.target?.value;
                  setEndDateOffSet(value ? parseInt(value, 10) : null);
                }}
              />
            </JobNameWrapper>
          </JobNameContainer>
        </Box>
      </Box>
      <Box>
        <Typography sx={headingText}>{t('jobManagement:punchGracePeriod')}</Typography>
        <PunchInOutGracePeriod
          punchInHours={punchInHours}
          setPunchInHours={setPunchInHours}
          punchInMinutes={punchInMinutes}
          setPunchInMinutes={setPunchInMinutes}
          punchOutHours={punchOutHours}
          setPunchOutHours={setPunchOutHours}
          punchOutMinutes={punchOutMinutes}
          setPunchOutMinutes={setPunchOutMinutes}
        />
      </Box>
    </>
  );
};

export default ScheduleDetails;
